// Canonical exhaustiveness helpers. See [[type-driven-design]] skill.

/**
 * Compile-time exhaustiveness probe. Use at the end of every `switch` on a
 * discriminator:
 *
 * ```ts
 * switch (state.kind) {
 *   case 'none': …
 *   // …all variants…
 *   default: return unreachable(state);
 * }
 * ```
 *
 * Adding a new variant to the union makes every `unreachable(x)` call where
 * `x` no longer narrows to `never` a TS compile error — the audit happens at
 * typecheck time, not in QA. Throws at runtime as a defence-in-depth signal
 * if the type system is bypassed (`as` cast, JSON-from-the-wire that escaped
 * the boundary parser, etc.).
 *
 * @see .tondocs/skills/type-driven-design.md §3 (Exhaustiveness checking with `never`)
 */
export function unreachable(x: never): never {
  throw new Error(`unreachable: ${JSON.stringify(x)}`);
}

/**
 * Labelled exhaustiveness probe — same `never`-typed compile-time guarantee
 * as {@link unreachable}, with a per-call-site contextual prefix in the
 * runtime error message so a hit in production points at the offending
 * switch (createOrder vs resolveCheckoutIntent vs …). Use when the same
 * union is switched on in multiple modules and you want stack-free triage.
 *
 * @example
 *   switch (ctx.kind) {
 *     case 'listing': …
 *     case 'offer':   …
 *     case 'auction': …
 *     default: return unreachableContext('createOrder', ctx);
 *   }
 */
export function unreachableContext(label: string, ctx: never): never {
  throw new Error(
    `${label}: unreachable context kind (${JSON.stringify(ctx)})`,
  );
}
